import { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
// Mui
import { Box, Tooltip } from '@mui/material';
// Icons
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
// Components
import { IconButton } from 'app/components/Mui';

interface Props {
  messageId?: string;
  activeIndex: number;
  onChangeActiveIndex: (index:number) => void;
  totalMessages: number;
}

let timeout:any = null;

export default function RefineMessageNavigation({
  messageId, activeIndex, onChangeActiveIndex, totalMessages
}:Props){
  const { t } = useTranslation();

  const boxRef = useRef<HTMLButtonElement | null>(null);

  const [ isClicked, setIsClicked ] = useState(false);
  const [ isBoxInView, setIsBoxInView ] = useState(true);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsBoxInView(entry.isIntersecting);
        });
      },
      { threshold: 1.0 }
    );

    if ( boxRef.current ) observer.observe(boxRef.current);
    return () => {
      if ( boxRef.current ) observer.unobserve(boxRef.current);
    };
  }, []);

  useEffect(() => {
    if ( !isClicked ) return;

    if ( isBoxInView ) return;

    if ( timeout ) clearTimeout(timeout);

    timeout = setTimeout(() => {
      if ( !boxRef.current ) return;
      boxRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setIsClicked(false);
    }, 250);
  }, [isClicked, isBoxInView]);

  const handlePrevNext = (next:boolean = false) => () => {
    !next ? onChangeActiveIndex(activeIndex - 1) : onChangeActiveIndex(activeIndex + 1);
    setIsClicked(true);
  }

  const isPrevButtonDisabled = activeIndex === 0;
  const isNextButtonDisabled = activeIndex === totalMessages - 1;

  if ( totalMessages <= 1 ) return null;
  return (
    <Box
      ref={boxRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}
    >
      <Tooltip title={t('buttons.prev')}>
        <span>
          <IconButton
            name={`Prev refine ${messageId}`}
            disabled={isPrevButtonDisabled}
            onClick={handlePrevNext()}
            size="small"
          ><ChevronLeftIcon fontSize="small"/></IconButton>
        </span>
      </Tooltip>
      <Tooltip title={t('buttons.next')}>
        <span>
          <IconButton
            name={`Next refine ${messageId}`}
            disabled={isNextButtonDisabled}
            onClick={handlePrevNext(true)}
            size="small"
          ><ChevronRightIcon fontSize="small" /></IconButton>
        </span>
      </Tooltip>
    </Box>
  )
}
